import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueSchemeDetailDesignerPageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        schemeDetail: {
          fields: {
            currentPublishedVersion: "当前发布版本",
            currentViewingVersion: "当前查看版本",
            unassignedStage: "未分阶段",
            metadataJson: "元数据 JSON",
            dispatchTargetType: "下发目标类型",
            completionConditionJson: "完成条件 JSON",
            nextTaskRulesJson: "后续任务规则 JSON",
            templateMarkdown: "模板 Markdown",
            reportTemplateTargetRole: "适用角色",
          },
          reportTemplateTargetRoles: {
            all: "全部角色",
          },
          markdownEditor: {
            emptyPreview: "暂无模板 Markdown。",
            modes: {
              edit: "编辑",
              split: "分屏",
              preview: "预览",
            },
            toolbar: {
              heading: "二级标题",
              bold: "加粗",
              italic: "斜体",
              quote: "引用",
              bulletList: "无序列表",
              orderedList: "有序列表",
              link: "链接",
              codeBlock: "代码块",
            },
            placeholders: {
              heading: "标题",
              bold: "加粗文本",
              italic: "斜体文本",
              quote: "引用内容",
              bulletList: "列表项",
              orderedList: "列表项",
              link: "链接文本",
              codeBlock: "代码",
            },
          },
          hints: {
            campTypeExamples: "允许值：red、blue 或 observer。",
            taskTypeExamples: "例如 manual、flag、subjective；前端不会再猜测任务类型。",
            dispatchModeExamples: "允许值：manual。",
            dispatchTargetTypeExamples: "例如 all、camp、seat、captain；留空表示没有专门目标类型。",
            scoreKindExamples: "允许值：flag、subjective。",
            stagesTasksWorkspace: "先在左侧维护阶段，再在右侧为当前阶段逐条新增或编辑任务。",
          },
          validation: {
            taskEditTargetMissing: "要编辑的任务不存在，请刷新方案工作台后重试。",
            reportTemplateEditTargetMissing: "要编辑的报告模板不存在，请刷新方案工作台后重试。",
            jsonInvalid: "{label} 必须是合法 JSON",
            jsonObjectRequired: "{label} 必须是 JSON 对象",
            jsonArrayRequired: "{label} 必须是 JSON 数组",
          },
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        schemeDetail: {
          fields: {
            currentPublishedVersion: "Current Published Version",
            currentViewingVersion: "Current Viewing Version",
            unassignedStage: "Unassigned",
            metadataJson: "Metadata JSON",
            dispatchTargetType: "Dispatch Target Type",
            completionConditionJson: "Completion Condition JSON",
            nextTaskRulesJson: "Next Task Rules JSON",
            templateMarkdown: "Template Markdown",
            reportTemplateTargetRole: "Applicable Role",
          },
          reportTemplateTargetRoles: {
            all: "All Roles",
          },
          markdownEditor: {
            emptyPreview: "No template Markdown yet.",
            modes: {
              edit: "Edit",
              split: "Split",
              preview: "Preview",
            },
            toolbar: {
              heading: "Heading",
              bold: "Bold",
              italic: "Italic",
              quote: "Quote",
              bulletList: "Bullet List",
              orderedList: "Ordered List",
              link: "Link",
              codeBlock: "Code Block",
            },
            placeholders: {
              heading: "Heading",
              bold: "bold text",
              italic: "italic text",
              quote: "quote",
              bulletList: "List item",
              orderedList: "List item",
              link: "Link text",
              codeBlock: "code",
            },
          },
          hints: {
            campTypeExamples: "Allowed values: red, blue, or observer.",
            taskTypeExamples:
              "Examples: manual, flag, or subjective. The frontend will not guess task types anymore.",
            dispatchModeExamples: "Allowed value: manual.",
            dispatchTargetTypeExamples:
              "Examples: all, camp, seat, or captain. Leave empty when no explicit target type is needed.",
            scoreKindExamples: "Allowed values: flag or subjective.",
            stagesTasksWorkspace:
              "Maintain stages on the left, then add or edit tasks one at a time for the selected stage on the right.",
          },
          validation: {
            taskEditTargetMissing:
              "The task to edit no longer exists. Refresh the workbench and try again.",
            reportTemplateEditTargetMissing:
              "The report template to edit no longer exists. Refresh the workbench and try again.",
            jsonInvalid: "{label} must be valid JSON.",
            jsonObjectRequired: "{label} must be a JSON object.",
            jsonArrayRequired: "{label} must be a JSON array.",
          },
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        schemeDetail: {
          fields: {
            currentPublishedVersion: "現在の公開版",
            currentViewingVersion: "現在表示中の版",
            unassignedStage: "未分類",
            metadataJson: "メタデータ JSON",
            dispatchTargetType: "配布対象種別",
            completionConditionJson: "完了条件 JSON",
            nextTaskRulesJson: "次タスク規則 JSON",
            templateMarkdown: "テンプレート Markdown",
            reportTemplateTargetRole: "適用ロール",
          },
          reportTemplateTargetRoles: {
            all: "全ロール",
          },
          markdownEditor: {
            emptyPreview: "テンプレート Markdown はまだありません。",
            modes: {
              edit: "編集",
              split: "分割",
              preview: "プレビュー",
            },
            toolbar: {
              heading: "見出し",
              bold: "太字",
              italic: "斜体",
              quote: "引用",
              bulletList: "箇条書き",
              orderedList: "番号付きリスト",
              link: "リンク",
              codeBlock: "コードブロック",
            },
            placeholders: {
              heading: "見出し",
              bold: "太字テキスト",
              italic: "斜体テキスト",
              quote: "引用内容",
              bulletList: "リスト項目",
              orderedList: "リスト項目",
              link: "リンクテキスト",
              codeBlock: "コード",
            },
          },
          hints: {
            campTypeExamples: "許可値: red、blue、observer。",
            taskTypeExamples:
              "例: manual、flag、subjective。フロントエンドはタスク種別をもう推測しません。",
            dispatchModeExamples: "許可値: manual。",
            dispatchTargetTypeExamples:
              "例: all、camp、seat、captain。明示的な対象種別が不要なら空のままにします。",
            scoreKindExamples: "許可値: flag、subjective。",
            stagesTasksWorkspace:
              "左側で段階を管理し、右側で選択中の段階にタスクを1件ずつ追加または編集します。",
          },
          validation: {
            taskEditTargetMissing:
              "編集対象のタスクが存在しません。ワークベンチを更新してから再試行してください。",
            reportTemplateEditTargetMissing:
              "編集対象のレポートテンプレートが存在しません。ワークベンチを更新してから再試行してください。",
            jsonInvalid: "{label} は有効な JSON である必要があります。",
            jsonObjectRequired: "{label} は JSON オブジェクトである必要があります。",
            jsonArrayRequired: "{label} は JSON 配列である必要があります。",
          },
        },
      },
    },
  },
};
